/**
 * Preference Row
 *
 * Single notification type with enabled toggle and toast/panel checkboxes.
 */

import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  getEffectivePreference,
  type NotificationType,
  type NotificationTypePreference,
} from "@/hooks/useNotificationPreferences";
import { cn } from "@/lib/utils";

interface PreferenceRowProps {
  type: NotificationType;
  disabled?: boolean;
  onChange: (
    typeKey: string,
    updates: Partial<NotificationTypePreference>,
  ) => void;
}

export function PreferenceRow({
  type,
  disabled,
  onChange,
}: PreferenceRowProps) {
  const isEnabled = getEffectivePreference(type, "enabled");
  const showToast = getEffectivePreference(type, "showToast");
  const showInPanel = getEffectivePreference(type, "showInPanel");

  return (
    <div
      className={cn(
        "flex items-center justify-between py-3 px-4",
        "border-b last:border-b-0",
        "hover:bg-accent/20 transition-colors",
        disabled && "opacity-50 pointer-events-none",
      )}
      style={{ borderColor: "hsl(var(--border))" }}
    >
      {/* Type info and enabled toggle */}
      <div className="flex items-center gap-3">
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled: boolean) =>
            onChange(type.typeKey, { enabled })
          }
          disabled={disabled}
          aria-label={`Enable ${type.displayName}`}
        />
        <div>
          <Label className="font-medium">{type.displayName}</Label>
          {type.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {type.description}
            </p>
          )}
        </div>
      </div>

      {/* Toast and Panel toggles */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <Checkbox
            checked={showToast}
            onCheckedChange={(checked) =>
              onChange(type.typeKey, { showToast: checked === true })
            }
            disabled={disabled || !isEnabled}
            aria-label={`Show toast for ${type.displayName}`}
            className="h-5 w-5 border-2"
            style={{ borderRadius: "4px", borderColor: "hsl(var(--border))" }}
          />
          <span
            className={cn(
              "text-sm font-medium text-foreground",
              (!isEnabled || disabled) && "text-muted-foreground",
            )}
          >
            Toast
          </span>
        </label>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <Checkbox
            checked={showInPanel}
            onCheckedChange={(checked) =>
              onChange(type.typeKey, { showInPanel: checked === true })
            }
            disabled={disabled || !isEnabled}
            aria-label={`Show in panel for ${type.displayName}`}
            className="h-5 w-5 border-2"
            style={{ borderRadius: "4px", borderColor: "hsl(var(--border))" }}
          />
          <span
            className={cn(
              "text-sm font-medium text-foreground",
              (!isEnabled || disabled) && "text-muted-foreground",
            )}
          >
            Panel
          </span>
        </label>
      </div>
    </div>
  );
}
